import React, { useEffect, useMemo } from 'react'
import { ToolComponentCtx } from '@hose/chat-app'
import MatchedCard from '../MatchedCard'
import { openFlowTemplateModal, showDialog } from '../../utils/postmate'
import { useConfig, useFlowTemplateSelection } from '../../hooks/useChatInitialization'
import { useMatchedData } from '../../hooks/useMatchedData'
import { ISpecificationMatched } from '../../types/dataMatched'
import { message } from '@hose/eui'
import { v1 as uuid } from 'uuid';
import { getParamFromUrl } from '../../utils/tools'


export const FlowTemplateMatched: React.FC<ToolComponentCtx> = (props) => {
	const { state, toolCallId } = props

	const config = useConfig();
	const { specifications } = config;

	const isMobile = useMemo(() => {
		return getParamFromUrl('platform') === 'mobile'
	}, [])

	// 使用通用的匹配数据管理 hook
	const {
		matchedData,
		setMatchedData,
		onReset,
		onConfirm,
		isHidden,
		countdown,
		isCountdown,
		cancelCountdown
	} = useMatchedData<ISpecificationMatched>(props, {
		defaultData: [],
		enableAutoSubmit: true, // 启用倒计时自动提交
		countdownSeconds: 10, // 10秒倒计时
		hideAfterSubmit: true, // 提交后隐藏组件
		enableCache: true, // 启用缓存功能
		parseData: (result) => {
			const specificationsMap: Record<string, any> = {}
			specifications?.forEach(item => {
				const key = item?.id?.split(':')?.shift()
				key && (specificationsMap[key] = item)
			})
			return Array.isArray(result) ? result.map(item => {
				return {
					...item,
					icon: specificationsMap[item.id]?.icon,
					color: specificationsMap[item.id]?.color,
				}
			}) : result
		}
	})



	// 使用新的 hook 监听单据模板选择事件
	const selectedTemplate = useFlowTemplateSelection();

	// 处理选中的模板数据
	useEffect(() => {
		if (selectedTemplate && selectedTemplate?.toolCallId === toolCallId) {
			console.log('📥 收到选中的单据模板:', selectedTemplate);

			// 处理选中的模板数据
			if (selectedTemplate) {
				// 如果是新增模板
				if (selectedTemplate.isNew) {
					// 添加新模板到列表
					const newTemplate: ISpecificationMatched = {
						id: selectedTemplate.id || `template_${uuid()}`,
						uuid: selectedTemplate.uuid || `uuid_${uuid()}`,
						icon: selectedTemplate.icon,
						color: selectedTemplate.color,
						templateName: selectedTemplate.templateName || '新模板',
						attachments: [],
						requisition: []
					};
					setMatchedData(prev => {
						return [...prev, newTemplate];
					});
				} else {
					// 如果是切换模板
					setMatchedData(prev => {
						const result = prev.map(item => {
							return item.uuid === selectedTemplate.originalUuid
								? {
									...item,
									icon: selectedTemplate.icon,
									color: selectedTemplate.color,
									id: selectedTemplate.id,
									templateName: selectedTemplate.templateName,
								}
								: item
						});
						console.log('切换单据模板成功', result)
						return result;
					});
				}
			}
		}
	}, [selectedTemplate, toolCallId, setMatchedData]);

	const disabledConfig = useMemo(() => {
		return {
			disabled: matchedData.length === 1,
			tooltip: matchedData.length === 1 ? '必须至少存在一个单据模板' : ''
		}
	}, [matchedData])

	if (state !== 'result') {
		return null
	}

	// 如果组件被隐藏，返回 null
	if (isHidden) {
		return null
	}

	if (state === 'result' && !props?.result) {
		return null
	}

	const handleAttachmentMove = (attachmentId: string, fromItemId: string, toItemId: string, insertIndex?: number) => {
		console.log('附件移动:', { attachmentId, fromItemId, toItemId, insertIndex })
		// 找到要移动的附件
		const sourceItem = matchedData.find(item => item.uuid === fromItemId)
		const attachmentToMove = sourceItem?.attachments.find(attachment => attachment.fileKey === attachmentId)

		if (!attachmentToMove) {
			console.log('未找到要移动的附件:', attachmentId)
			return
		}

		setMatchedData(prev => {
			return prev.map(item => {
				if (item.uuid === fromItemId && item.uuid === toItemId) {
					// 同容器内排序
					console.log('同容器内排序:', { itemId: item.id, insertIndex })
					const attachments = [...item.attachments]
					const oldIndex = attachments.findIndex(att => att.fileKey === attachmentId)

					console.log('排序前:', attachments.map(att => att.fileName))
					console.log('旧位置:', oldIndex, '新位置:', insertIndex)

					if (insertIndex !== undefined && insertIndex >= 0 && oldIndex !== insertIndex) {
						// 移除原位置的附件
						attachments.splice(oldIndex, 1)
						// 插入到新位置
						attachments.splice(insertIndex, 0, attachmentToMove)
						console.log('排序后:', attachments.map(att => att.fileName))
					} else {
						// 如果没有指定位置或位置相同，保持原样
						console.log('位置相同或无效，保持原样')
						return item
					}

					return { ...item, attachments }
				} else if (item.uuid === fromItemId) {
					// 从源容器移除附件
					console.log('从源容器移除附件:', item.id)
					return { ...item, attachments: item.attachments.filter(attachment => attachment.fileKey !== attachmentId) }
				} else if (item.uuid === toItemId) {
					// 添加到目标容器
					console.log('添加到目标容器:', item.id, '插入位置:', insertIndex)
					const newAttachments = [...item.attachments]

					if (insertIndex !== undefined && insertIndex >= 0) {
						// 插入到指定位置
						newAttachments.splice(insertIndex, 0, attachmentToMove)
						console.log('插入到位置:', insertIndex)
					} else {
						// 添加到末尾
						newAttachments.push(attachmentToMove)
						console.log('添加到末尾')
					}

					console.log('目标容器最终附件:', newAttachments.map(att => att.fileName))
					return { ...item, attachments: newAttachments }
				}
				return item
			})
		})

		console.log('附件移动完成')
	}

	const onDelete = (dataSource: ISpecificationMatched) => {
		console.log('删除:', dataSource)

		// 检查是否有附件
		const isHasAttachments = 'attachments' in dataSource ? dataSource.attachments.length > 0 : false
		if (isHasAttachments) {
			const content = '请先将当前模板下的文件移动到其他单据模板区域'
			if(isMobile){
				showDialog({
					type:'alert',
					content
				})
			} else {
				message.error(content)
			}
			return
		}

		// 过滤掉要删除的项目
		const newMatchedData = matchedData.filter(item => item.uuid !== dataSource.uuid)
		setMatchedData(newMatchedData)
	}

	const handleAdd = async () => {
		console.log('新增单据模板')
		try {
			await openFlowTemplateModal({
				toolCallId,
				isNew: true,
				action: 'add'
			})
		} catch (error) {
			console.error('新增单据模板失败:', error)
		}
	}

	const onChange = async (dataSource: ISpecificationMatched) => {
		console.log('切换模板')
		try {
			await openFlowTemplateModal({
				toolCallId,
				dataSource,
				isNew: false,
				action: 'change',
				originalUuid: dataSource.uuid
			})
		} catch (error) {
			console.error('切换模板失败:', error)
		}
	}

	return (
		<MatchedCard
			cardTitle="确认单据模板"
			matchedData={matchedData}
			addButtonText="新增单据模板"
			deleteDisabled={disabledConfig.disabled}
			disableTooltip={disabledConfig.tooltip}
			enableDrag={true}
			isCountdown={isCountdown}
			countdown={countdown}
			onCancelCountdown={cancelCountdown}
			onAttachmentMove={handleAttachmentMove}
			onAdd={handleAdd}
			onDelete={onDelete}
			onChange={onChange}
			onConfirm={onConfirm}
			onReset={onReset}
		/>
	)
}

export default FlowTemplateMatched